import { motion } from 'framer-motion';
import { MessageCircle, AtSign, ExternalLink } from 'lucide-react';
import Navigation from '../components/Navigation';
import WhatsAppFloat from '../components/WhatsAppFloat';
import SEO from '../components/SEO';
import { OrganizationSchema, BreadcrumbSchema } from '../utils/schemas';

function ContactoPage() {
  const handleWhatsAppClick = () => {
    const number = process.env.REACT_APP_WHATSAPP_NUMBER || '50767541097';
    const message = encodeURIComponent(process.env.REACT_APP_WHATSAPP_MESSAGE || 'Hola, me interesa conocer más sobre tu trabajo fotográfico');
    window.open(`https://wa.me/${number}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO
        title="Contacto — EMABRAW | Edgardo Andrade"
        description="Contacta a Edgardo Andrade para colaboraciones fotográficas, exposiciones, asignaciones editoriales y trabajo comisionado. Disponible vía WhatsApp e Instagram."
        image="/og-contacto.jpg"
        type="website"
        keywords="contacto fotógrafo Panamá, Edgardo Andrade contacto, contratar fotógrafo documental, colaboraciones fotografía"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            OrganizationSchema,
            BreadcrumbSchema([
              { name: "Inicio", url: "https://www.emabraw.com" },
              { name: "Contacto", url: "https://www.emabraw.com/contacto" }
            ])
          ]
        }}
      />

      <Navigation />
      <WhatsAppFloat />

      <section className="px-6 sm:px-12 lg:px-24 pt-32 pb-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-4 text-center"
          >
            <p className="text-xs tracking-[0.2em] uppercase text-neutral-500">Conectemos</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-white">
              Contacto
            </h1>
            <p className="text-base leading-relaxed text-neutral-400 max-w-2xl mx-auto">
              Disponible para exposiciones, asignaciones editoriales y trabajo comisionado.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="px-6 sm:px-12 lg:px-24 pb-24">
        <div className="max-w-2xl mx-auto">
          <div className="grid gap-6">
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              onClick={handleWhatsAppClick}
              className="group bg-neutral-900 border border-neutral-800 p-8 hover:border-green-500 transition-all duration-300 text-left"
              data-testid="whatsapp-contact-btn"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-green-500 p-3 rounded-full group-hover:scale-110 transition-transform">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl text-white font-medium">WhatsApp</h3>
                  </div>
                  <p className="text-neutral-400 text-sm mb-3">
                    La forma más rápida de conectar para colaboraciones y proyectos.
                  </p>
                  <p className="text-neutral-500 text-sm">+507 6754-1097</p>
                </div>
                <ExternalLink className="w-5 h-5 text-neutral-600 group-hover:text-green-500 transition-colors" />
              </div>
            </motion.button>

            <motion.a
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              href="https://www.instagram.com/emabraw"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-neutral-900 border border-neutral-800 p-8 hover:border-pink-500 transition-all duration-300"
              data-testid="instagram-link"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 p-3 rounded-full group-hover:scale-110 transition-transform">
                    <AtSign className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl text-white font-medium">Instagram</h3>
                  </div>
                  <p className="text-neutral-400 text-sm mb-3">
                    Sígueme en Instagram para ver mi trabajo más reciente y actualizaciones.
                  </p>
                  <p className="text-neutral-500 text-sm">@emabraw</p>
                </div>
                <ExternalLink className="w-5 h-5 text-neutral-600 group-hover:text-pink-500 transition-colors" />
              </div>
            </motion.a>

            <motion.a
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              href="https://cuanto.app/emabraw.pa"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-neutral-900 border border-neutral-800 p-8 hover:border-white transition-all duration-300"
              data-testid="tienda-link"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-white p-3 rounded-full group-hover:scale-110 transition-transform">
                      <ExternalLink className="w-6 h-6 text-black" />
                    </div>
                    <h3 className="text-xl text-white font-medium">Tienda</h3>
                  </div>
                  <p className="text-neutral-400 text-sm mb-3">
                    Explora y adquiere impresiones de mi trabajo fotográfico.
                  </p>
                  <p className="text-neutral-500 text-sm">cuanto.app/emabraw.pa</p>
                </div>
                <ExternalLink className="w-5 h-5 text-neutral-600 group-hover:text-white transition-colors" />
              </div>
            </motion.a>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 text-center space-y-4"
          >
            <p className="text-sm text-neutral-500">Ubicación</p>
            <p className="text-neutral-300">Panamá, República de Panamá</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default ContactoPage;
